import { Event } from '@sentry/types';

import { APP_VERSION, NODE_ENV } from '../../config/common.config';
import { SENTRY_URL, DEPLOYMENT_ENVIRONMENT } from '../../config/client.config';

class CustomError extends Error {
  public constructor({ name, message, stack }) {
    super(message);
    this.name = name;
    if (stack) {
      this.stack = stack;
    }
  }
}

interface TagInterface {
  name: string;
  value: string;
}

interface CaptureErrorInterface {
  name: Error['name'];
  message: Error['message'];
  stack?: Error['stack'];
  tags?: TagInterface[];
  extra?: Event['extra'];
}

const importSentry = () =>
  import('@sentry/browser').then((imported) => imported.default);

let Sentry: UnwrapPromiseLike<ReturnType<typeof importSentry>>;

export function loadSentry(): void {
  if (NODE_ENV === 'development') return;

  importSentry()
    .then((SentryImport) => {
      Sentry = SentryImport;
      Sentry.init({
        dsn: SENTRY_URL,
        environment: DEPLOYMENT_ENVIRONMENT,
      });

      Sentry.configureScope((scope) => {
        scope.setTag('app_version', APP_VERSION);
        scope.setTag('app_namespace', window.location.pathname.split('/')[2]);
        scope.setTag('error_type', 'genericError');
      });
    })
    .catch();
}

export default ({
  name,
  message,
  stack,
  tags,
  extra,
}: CaptureErrorInterface): void => {
  Sentry?.withScope((scope) => {
    const error = new CustomError({
      name,
      message,
      stack,
    });

    if (tags) {
      tags.forEach(({ name: tagName, value }) => scope.setTag(tagName, value));
    }
    if (extra) {
      scope.setExtra('additionalData', extra);
    }

    Sentry.captureException(error);
  });
};
